import { useCallback, useContext, useEffect, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { getGroupMembers } from '../../../services/groupsService'
import { getMePersons } from '../../../services/treeService'
import { deepCopy } from '../../../util/Utils'
import { GroupContext } from '../GroupContextProvider'

// Constants
const actions = {
  REFRESH_DATA: 'REFRESH_DATA',
  SET_CHECKED_MEMBERS: 'SET_CHECKED_MEMBERS',
  SET_GROUP_MEMBERS_DATA: 'SET_GROUP_MEMBERS_DATA',
  SET_ME_PERSONS: 'SET_ME_PERSONS',
}

function groupMembersReducer(oldState, action) {
  const newState = deepCopy(oldState)
  const { payload, type } = action

  switch (type) {
    case actions.REFRESH_DATA: {
      newState.members = null
      newState.selectedMembers = []
      newState.refreshMemberDataToggle = !newState.refreshMemberDataToggle
      break
    }
    case actions.SET_GROUP_MEMBERS_DATA: {
      newState.members = payload.members
      break
    }
    case actions.SET_CHECKED_MEMBERS: {
      newState.selectedMembers = newState.members.filter((member) => payload.includes(member.memberCisId))
      break
    }
    case actions.SET_ME_PERSONS: {
      newState.mePersons = { ...newState.mePersons, ...payload }
      break
    }
    default:
      break
  }
  return newState
}

export default function useGroupMembers() {
  const { error } = useNotifications()
  const { currentGroupData } = useContext(GroupContext)
  const { t } = useTranslation()

  // Initial State
  const initialState = {
    // controls
    refreshMemberDataToggle: false,
    selectedMembers: [],
    // data
    members: null,
    mePersons: {},
  }
  // Reducer Hook
  const [groupMembersState, groupMembersDispatch] = useReducer(groupMembersReducer, initialState)
  // Effects
  useEffect(() => {
    let isCurrent = true
    if (currentGroupData.id) {
      getGroupMembers(currentGroupData.id)
        .then((response) => {
          if (isCurrent) {
            groupMembersDispatch({ type: actions.SET_GROUP_MEMBERS_DATA, payload: response.data })
            groupMembersDispatch({ type: actions.SET_CHECKED_MEMBERS, payload: [] })
          }
        })
        .catch((err) => {
          error(
            t('groups.errors.retrievingGroupMembers', {
              defaultValue: `Error retrieving group members: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    }
    return () => {
      isCurrent = false
    }
  }, [error, currentGroupData.id, groupMembersState.refreshMemberDataToggle, t])
  useEffect(() => {
    currentGroupData.treeIds?.forEach((treeId) => {
      getMePersons(treeId.value)
        .then((response) => {
          groupMembersDispatch({ type: actions.SET_ME_PERSONS, payload: response.data })
        })
        .catch((err) => {
          error(
            t('groups.errors.retrievingMePersons', {
              defaultValue: `Error retrieving me persons for treeId: ${treeId.value}, ${err.message}`,
              treeId: treeId.value,
              message: err.message || '',
            }),
            err
          )
        })
    })
  }, [error, currentGroupData.treeIds, t])
  // Functions
  const setCheckedMembers = useCallback(
    (checkedItems) => {
      if (groupMembersState.selectedMembers.length !== checkedItems.length) {
        groupMembersDispatch({ type: actions.SET_CHECKED_MEMBERS, payload: checkedItems })
      }
    },
    [groupMembersState.selectedMembers.length]
  )
  const refreshData = () => {
    groupMembersDispatch({ type: actions.REFRESH_DATA })
  }
  // Variables
  // Helpers
  const helpers = {
    refreshData,
    setCheckedMembers,
  }
  return [groupMembersState, helpers]
}
